import { hasSubmitFailed } from 'redux-form';
import { SQ_ANALYTICS_TOKEN } from '../../constants';
import { CHECKOUT_FORM_NAME } from '../../constants/checkoutForm';
import { PaymentMethodType } from '../../models/paymentMethods/enums';
import { hasVirtualIncentivesErrors } from '../../selectors/order';
import { getUnformattedOrderCost } from '../../selectors/order/getUnformattedOrderCost';
import { getIsPayingWithVirtualIncentives } from '../../selectors/payment';
import { getUserInstruments, isUserLoggedIn } from '../../selectors/user';
import {
    getHasPartiallyPaidWithVirtualIncentives,
    getSelectedVirtualIncentives,
} from '../../selectors/virtualIncentive';
import { CheckoutState } from '../../state';
import { getSelectedVirtualIncentivesAggregatedTotal } from '../../utils/virtualIncentives';
import { userSelectedStoredPaymentMethod } from './utils';

export interface FormData {
    paymentMethod: PaymentMethodType;
    payment?: {
        vaultId?: string | PaymentMethodType.CREDIT;
    };
}

/*
 * When a stored payment method has been selected, we have to tweak the formData to use the
 * USER_INSTRUMENT payment method and include the data for the selected instrument.
 */
const getStoredPaymentMethodAdditionalData = ({
    formData,
    state,
}: {
    formData: FormData;
    state: CheckoutState;
}) => {
    const userInstruments = getUserInstruments(state);

    const payment = formData.payment;
    if (!payment) return {};

    const isPayingWithStoredPaymentMethod =
        isUserLoggedIn(state) &&
        userSelectedStoredPaymentMethod({
            paymentMethod: formData.paymentMethod,
            payment,
        });

    return isPayingWithStoredPaymentMethod
        ? {
              paymentMethod: PaymentMethodType.USER_INSTRUMENT,
              userInstrument: userInstruments.find(
                  (instrument) => instrument.vaultId === payment.vaultId,
              ),
          }
        : {};
};

export const getSaveOrderFormData = ({
    formData,
    state,
}: {
    formData: FormData;
    state: CheckoutState;
}) => ({
    ...formData,
    ...getStoredPaymentMethodAdditionalData({ formData, state }),
});

const getVirtualIncentivesAdditionalData = (state: CheckoutState) => {
    /*
     * If a previous submission failed we don't want to use Virtual Incentives anymore because:
     *
     * - If payment using Virtual Incentives failed we don't want to get blocked by VI provider so we just disable VI.
     * - If payment using VI succeeded then the order is partially paid and the VI are already consumed so we can't use them anymore.
     */
    const shouldPayWithVirtualIncentives =
        getIsPayingWithVirtualIncentives(state) &&
        !getHasPartiallyPaidWithVirtualIncentives(state);

    return shouldPayWithVirtualIncentives
        ? {
              virtualIncentives: getSelectedVirtualIncentives(state),
          }
        : {};
};

const getPaymentAdditionalData = ({
    formData,
    state,
}: {
    formData: FormData;
    state: CheckoutState;
}) =>
    formData.payment
        ? {
              payment: {
                  ...formData.payment,
                  [SQ_ANALYTICS_TOKEN]: state.tokens[SQ_ANALYTICS_TOKEN],
                  deferredPaymentDueDays: state.app.deferredPaymentDueDays,
              },
          }
        : {
              payment: {},
          };

const getTotalAmountInCents = (state: CheckoutState) => {
    const totalOrderCostInCents = getUnformattedOrderCost(state);
    const selectedVirtualIncentives = getSelectedVirtualIncentives(state);

    const hasNonVirtualIncentivePaymentMethodFailed =
        !hasVirtualIncentivesErrors(state) &&
        hasSubmitFailed(CHECKOUT_FORM_NAME)(state) &&
        selectedVirtualIncentives.length === 0;

    /*
     * If Virtual Incentives have not failed but the non-VI payment method has failed then the order is partially paid
     * so the total to be sent to the backend is lower. However, in the frontend we don't update the selected VI status
     * so we better just ignore them instead of relying on having a proper reflection of this edge case in the state
     */
    return hasNonVirtualIncentivePaymentMethodFailed
        ? totalOrderCostInCents -
              getSelectedVirtualIncentivesAggregatedTotal(
                  selectedVirtualIncentives,
              )
        : totalOrderCostInCents;
};

export const getSavePaymentMethodsFormData = ({
    formData,
    state,
}: {
    formData: FormData;
    state: CheckoutState;
}) => ({
    ...formData,
    ...getStoredPaymentMethodAdditionalData({ formData, state }),
    ...getVirtualIncentivesAdditionalData(state),
    ...getPaymentAdditionalData({ formData, state }),
    totalAmountInCents: getTotalAmountInCents(state),
});

export const getPlaceOrderFormData = ({
    formData,
    state,
}: {
    formData: FormData;
    state: CheckoutState;
}) => ({
    ...formData,
    ...getStoredPaymentMethodAdditionalData({ formData, state }),
    ...getPaymentAdditionalData({ formData, state }),
    // Heads up! totalAmountInCents is needed to check whether 3DS is required or not!
    totalAmountInCents: getTotalAmountInCents(state),
});
